import { identifyUser } from '@/app/bootstrap/clarity';
import { useNetError } from '@/app/hooks/useNetError';
import { AppRoutes } from '@/app/router/AppRoutes';
import { useScopeFromUrl } from '@/app/router/useScopeFromUrl';
import { Header } from '@/app/shell/Header';
import { logger } from '@/shared/helpers/logger';
import { useAppSelector } from '@/shared/hooks/useStore';
import { useTrans } from '@/shared/hooks/useTranslation';
import { AppErrorFallback, Fallback } from '@/view/shared/Fallback';
import { Loader } from '@/view/shared/Loader';
import { ShellHeaderProvider } from '@/view/shared/ShellHeader';
import { useOrganizationData } from '@/viewmodel/features/user';
import { useIdTokenClaimsPromise } from '@/viewmodel/features/user';
import { useLogtoStarting } from '@/viewmodel/features/user';
import { useUserData } from '@/viewmodel/features/user';
import { selectUserData } from '@/viewmodel/features/user';
import * as Sentry from '@sentry/react';
import { LazyMotion, domAnimation } from 'framer-motion';
import { type ReactNode, Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

function App(): ReactNode {
  const { t } = useTrans();
  const isLogtoStarting = useLogtoStarting();
  useUserData();
  useOrganizationData();
  useIdTokenClaimsPromise();
  const isOnline = useNetError();
  const userData = useAppSelector(selectUserData);

  useScopeFromUrl();

  useEffect(() => {
    if (userData.data?.id) identifyUser(userData.data.id);
  }, [userData.data?.id]);

  if (!isOnline) {
    return <Fallback message={t('common.error.offline')} />;
  }

  if (isLogtoStarting) {
    return <Loader />;
  }

  return (
    <LazyMotion features={domAnimation}>
      <div className="flex overflow-hidden h-dvh">
        <div className="flex-1 overflow-y-auto min-w-0">
          <ErrorBoundary
            onError={(error) =>
              logger.error('App ErrorBoundary caught error', { error, log_source: 'App' })
            }
            FallbackComponent={AppErrorFallback}
          >
            <Suspense fallback={<Loader />}>
              <ShellHeaderProvider header={<Header showLogo={false} />}>
                <AppRoutes />
              </ShellHeaderProvider>
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </LazyMotion>
  );
}

export default Sentry.withProfiler(App);
